import React, { useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useEntityList, createEntity, deleteEntity, roleSatisfies, useOrgProjects } from '../../data/entities';
import NewWorkflowForm, { friendlyWriteError } from '../../components/NewWorkflowForm';
import { askNova } from '../../components/ConsoleHeader';
import { STARTER_PROMPTS } from '../../data/consoleModules';
import LifecycleRail, { useLifecycleSignals } from '../../components/LifecycleRail';
import { useSelectedApp } from '../../components/AppSelector';
import { useDoc } from '../../data/firestore';
import { StatusBadge } from './AppsModule';
import AdminDashboard from './AdminDashboard';

// Org home. Admins and owners land on the live dashboard (usage, cost,
// attention, health, activity). Everyone else still gets the builder
// front door from the 2026-10 audit: a newcomer who wants "an app" should
// not land on workspaces and miss the path.
const BUILD_PROMPT = STARTER_PROMPTS.find((p) => p.attach) || STARTER_PROMPTS[0];

export default function Overview() {
  const { myRole } = useOutletContext();
  if (myRole === undefined) return <p className="cs-help">Loading…</p>;
  if (roleSatisfies(myRole, 'admin')) return <AdminDashboard />;
  return <MemberHome />;
}

function MemberHome() {
  const { org, myRole } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });
  const projects = useOrgProjects(org.id);
  const { docs: apps } = useEntityList('appInstance', { org: org.id });
  const { docs: allBuilderApps } = useEntityList('app', { org: org.id });
  const builderApps = allBuilderApps && allBuilderApps.filter((a) => !a.archived);
  const { docs: knowledgeDocs } = useEntityList('knowledgeDoc', { org: org.id });
  const { docs: bizProcesses } = useEntityList('bizProcess', { org: org.id }, { workspace: null });
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // First-run checklist, in the words a newcomer uses. Shown until every
  // required step is done; steps read the builder apps' links — an app is
  // the spine, so "built" means linked to an app, not merely existing.
  const linked = (key) => (builderApps || []).some((a) => a.links?.[key]);
  // The app in focus (sidebar switcher) is the one Home talks about; with
  // no focus the newest app stands in for the lifecycle caption only.
  const { app: focused } = useSelectedApp();
  const newest = focused || [...(builderApps || [])].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0] || null;
  const firstApp = newest;
  const lifeSignals = useLifecycleSignals(org, newest);
  const appQ = firstApp ? `?app=${firstApp.id}` : '';
  const steps = [
    { done: !!bizProcesses?.length, label: 'Map your business', hint: 'the systems, data, processes and people nventr will work on — Blueprint, or tell NoVA "map my business"', to: `/orgs/${org.id}/blueprint` },
    { done: !!builderApps?.length, label: 'Say what the app should do', hint: 'tell NoVA, or give it a name and goal in App Builder', to: `/orgs/${org.id}/builder` },
    { done: (knowledgeDocs || []).some((d) => (d.appIds || []).some((id) => (builderApps || []).some((a) => a.id === id))), label: 'Add the documents it should know', hint: 'PDFs, Word files, text, or a web page — in Knowledge', to: `/orgs/${org.id}/knowledge${appQ}` },
    { done: (builderApps || []).some((a) => a.links?.agentIds?.length), label: 'Give it an agent to talk to people', hint: 'its greeting, instructions and what it may use — App Builder → Design', to: `/orgs/${org.id}/builder${appQ}` },
    { done: linked('pipelineId'), optional: true, label: 'Give it steps to follow (optional)', hint: 'a workflow in Workflows — or ask NoVA to draft one', to: `/orgs/${org.id}/workflows${appQ}` },
    { done: linked('instanceAppId') || (!builderApps?.length && !!apps?.length), label: 'Put it in front of people', hint: 'on your website, by API, or as a hosted app — App Builder → Lab › Surfaces', to: `/orgs/${org.id}/builder${appQ ? `${appQ}&` : '?'}tab=lab&phase=surfaces` },
  ];
  const showChecklist = builderApps !== null && steps.some((s) => !s.done && !s.optional);
  // Hosted instances the org owns, minus the noise: terminated / consumed
  // docs and warm-pool blanks (pool-*) are the provisioner's, not the user's.
  const hosted = (apps || [])
    .filter((a) => !['terminated', 'consumed'].includes(a.status) && !/^pool-/.test(a.slug || ''))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const hostedUrl = (a) => a.urls?.app || (a.slug && !(a.deployment?.target && a.deployment.target !== 'managed') ? `https://${a.slug}.apps.nventr.io` : null);
  // Platform status: scripts/team-smoke.mjs loads every module as every
  // member after each release and writes the verdict here.
  const { doc: status } = useDoc('platformStatus/latest');

  return (
    <div>
      <h1 style={{ margin: 0 }}>{org.name}</h1>

      <section className="cs-card pf-hero" aria-label="Build an app">
        <h2>Build an app</h2>
        <p className="cs-help">
          Describe what you want and attach the documents it should know — NoVA
          plans the work and asks you before changing anything. Or define it
          yourself in App Builder and fill in the pieces step by step.
        </p>
        <div className="pf-hero-actions">
          <button type="button" className="cs-btn cs-btn-primary" onClick={() => askNova(BUILD_PROMPT.text, { attach: true })}>
            ◈ Build it with NoVA
          </button>
          <Link to={`/orgs/${org.id}/builder`} className="cs-btn cs-btn-outline">Start in App Builder</Link>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={() => askNova('Show me around: what are the parts of this console and what should I do first?')}>
            Show me around
          </button>
        </div>
        {!!builderApps?.length && (
          <p className="cs-help" style={{ margin: '10px 0 0' }}>
            Your apps: {builderApps.slice(0, 6).map((a, i) => (
              <React.Fragment key={a.id}>{i > 0 && ' · '}<Link to={`/orgs/${org.id}/builder?app=${a.id}`}>{a.name || 'Untitled'}</Link></React.Fragment>
            ))}
          </p>
        )}
      </section>

      {hosted.length > 0 && (
        <section className="cs-card pf-hosted" aria-label="Hosted instances">
          <div className="pf-hosted-head">
            <h2>Hosted instances</h2>
            <Link to={`/orgs/${org.id}/deployments?tab=instances`} className="cs-help">All instances →</Link>
          </div>
          <ul className="pf-hosted-list">
            {hosted.map((a) => {
              const url = a.status === 'live' ? hostedUrl(a) : null;
              return (
                <li key={a.id} className="pf-hosted-row">
                  <Link to={`/orgs/${org.id}/apps/${a.id}`} className="pf-hosted-name">{a.name || a.slug}</Link>
                  {a.envRole === 'uat' && <span className="pf-preview-tag" style={{ marginLeft: 0 }}>UAT</span>}
                  <StatusBadge status={a.status} />
                  {url
                    ? <a href={url} target="_blank" rel="noreferrer" className="cs-help pf-hosted-url">{url.replace(/^https?:\/\//, '')}</a>
                    : <span className="cs-help pf-hosted-url">{a.slug ? `${a.slug}.apps.nventr.io` : '—'}</span>}
                  <Link to={`/orgs/${org.id}/apps/${a.id}`} className="cs-help">Manage →</Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {status && (
        <section className="cs-card" aria-label="Platform status" style={{ padding: '10px 14px', marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className={`mf-chip ${status.okModules === status.totalModules ? 'is-ok' : 'is-warn'}`}>
            {status.okModules}/{status.totalModules} modules ok
          </span>
          <span className="cs-help" style={{ margin: 0, fontSize: 12 }}>
            Platform status · every module loaded as {status.members} team member{status.members === 1 ? '' : 's'} · checked {new Date(status.checkedAt).toLocaleString()}
          </span>
          {status.okModules !== status.totalModules && (
            <span className="cs-help" style={{ margin: 0, fontSize: 12 }}>
              attention: {Object.entries(status.modules || {}).filter(([, m]) => !m.ok).map(([id]) => id).join(', ')}
            </span>
          )}
        </section>
      )}

      <section className="cs-card" aria-label="App lifecycle" style={{ padding: '12px 14px', marginTop: 12 }}>
        <LifecycleRail
          variant="links"
          orgId={org.id}
          app={newest}
          signals={lifeSignals}
          compact
          caption={newest?.name || 'An app moves through these stages. The next change starts in the Lab.'}
        />
      </section>

      {showChecklist && (
        <section className="cs-card pf-checklist" aria-label="Getting started">
          <h2>How an app comes together</h2>
          <ol>
            {steps.map((st) => (
              <li key={st.label} className={st.done ? 'is-done' : ''}>
                <span className="pf-check" aria-hidden="true">{st.done ? '✓' : ''}</span>
                <span>
                  {st.to && !st.done ? <Link to={st.to}>{st.label}</Link> : st.label}
                  <span className="cs-help"> — {st.hint}</span>
                </span>
              </li>
            ))}
          </ol>
          <p className="cs-help" style={{ marginTop: 10, marginBottom: 0 }}>
            Stuck at any point? Ask NoVA (bottom-right) “how do I…” — it answers for this console and can take you to the
            right screen. Or <Link to={`/orgs/${org.id}/solutions`}>see example apps</Link> built on this platform.
          </p>
        </section>
      )}

      <h2 style={{ fontSize: 15, margin: '24px 0 4px' }}>Workspaces</h2>
      <p className="cs-help" style={{ margin: 0 }}>
        Optional organization for bigger teams: a workspace groups projects, workflows and compute (a team, a product,
        a client). Pick a project here or in the sidebar to work on it across every tool. Or name a workflow and the
        Workflows canvas opens with a workspace made for it:
      </p>
      <div style={{ marginTop: 10, maxWidth: 480 }}>
        <NewWorkflowForm orgId={org.id} workspaces={workspaces} placeholder="New workflow name…" buttonLabel="Build a workflow" />
      </div>

      <form
        style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 16, maxWidth: 480 }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim() || busy) return;
          setBusy(true);
          setError('');
          try {
            const id = await createEntity('workspace', { org: org.id }, {
              name: name.trim(),
              createdBy: user.uid,
            });
            setName('');
            navigate(`/orgs/${org.id}/ws/${id}`);
          } catch (err) {
            setError(friendlyWriteError(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="cs-input"
            placeholder="New workspace name…"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button className="cs-btn cs-btn-primary" disabled={!name.trim() || busy} type="submit">
            Create
          </button>
        </div>
        {error && <p className="cs-form-error" role="alert">{error}</p>}
      </form>

      {workspaces && !workspaces.length && (
        <div className="cs-empty" style={{ marginTop: 16 }}>No workspaces yet — you don’t need one to build an app. Add one when you want to group work (a team, a product, a client).</div>
      )}
      <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
        {(workspaces || []).map((ws) => (
          <div key={ws.id} className="cs-card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Link to={`/orgs/${org.id}/ws/${ws.id}`} style={{ textDecoration: 'none' }}>
                <strong style={{ color: 'var(--text)' }}>{ws.name}</strong>
              </Link>
              <span style={{ flex: 1 }} />
              {roleSatisfies(myRole, 'admin') && (
                <button
                  className="cs-btn cs-btn-outline cs-btn-sm"
                  onClick={async () => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm(`Delete workspace "${ws.name}"?`)) {
                      await deleteEntity('workspace', { org: org.id }, ws.id);
                    }
                  }}
                >
                  Delete
                </button>
              )}
            </div>
            {ws.description && <p className="cs-help" style={{ marginTop: 6 }}>{ws.description}</p>}
            <WorkspaceProjectChips orgId={org.id} ws={ws} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Projects inside a workspace card — each chip opens the project-scoped view.
function WorkspaceProjectChips({ orgId, ws }) {
  const { docs: projects } = useEntityList('project', { org: orgId, workspace: ws.id });
  if (!projects || !projects.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
      {projects.map((p) => (
        <Link key={p.id} to={`/orgs/${orgId}/p/${ws.id}/${p.id}`} className="pf-proj-chip">
          {p.name}
        </Link>
      ))}
    </div>
  );
}
